import { useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { fetcher } from "@/lib/api";
import EsgKpiCard from "@/components/esg/EsgKpiCard";
import {
  MonthlyEvolutionChart,
  ByConcelloBar,
  ComparativeBar,
} from "@/components/esg/EsgCharts";
import EsgDownloads from "@/components/esg/EsgDownloads";
import ScopeBanner from "@/components/salnes/ScopeBanner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import "@/i18n/fase2";

export default function EsgDashboard() {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState("last_30_days");
  const [concello, setConcello] = useState("all");

  const { data: indicatorsData } = useSWR("/salnes/esg/indicators", fetcher);
  const { data: rollups } = useSWR(
    `/salnes/esg/rollups?period=${period}&concello=${concello}`,
    fetcher,
    { revalidateOnFocus: false }
  );
  const { data: municipalities } = useSWR("/salnes/municipalities", fetcher);
  const { data: monthly } = useSWR("/salnes/analytics/monthly-history", fetcher);
  const { data: detectionsList } = useSWR(
    "/salnes/detections?limit=500",
    fetcher,
    { revalidateOnFocus: false }
  );

  const indicators = indicatorsData?.indicators || [];
  const concellos = municipalities?.concellos || [];
  const values = rollups?.values || {};

  const byConcello = useMemo(() => {
    // Since upstream tags all detections municipality="OSalnes", distribute by proximity to concello center.
    const dets = detectionsList?.detections || [];
    const counts = concellos.map((c) => ({ ...c, detections: 0 }));
    if (counts.length === 0) return counts;
    for (const d of dets) {
      const lat = d.latitude, lon = d.longitude;
      if (!lat || !lon) continue;
      let nearestIdx = 0, bestDist = Infinity;
      counts.forEach((c, i) => {
        if (!c.center) return;
        const dLat = c.center[0] - lat, dLon = c.center[1] - lon;
        const dist = dLat * dLat + dLon * dLon;
        if (dist < bestDist) { bestDist = dist; nearestIdx = i; }
      });
      if (counts[nearestIdx]) counts[nearestIdx].detections += 1;
    }
    return counts;
  }, [detectionsList, concellos]);

  const provisional = rollups?.notes?.provisional_indicators || [];
  const blockedList = rollups?.notes?.blocked_indicators || [];
  const activeList = rollups?.notes?.active_indicators || [];
  const totalKpis = activeList.length + provisional.length + blockedList.length;

  return (
    <div className="max-w-[1400px]" data-testid="esg-root">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
            {t("fase2.nav_esg")}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-salnes-ink leading-tight">
            {t("fase2.esg_title")}
          </h1>
          <p className="mt-2 text-salnes-stone">{t("fase2.esg_subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-10 w-[180px]" data-testid="esg-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days">{t("fase2.last_7d")}</SelectItem>
              <SelectItem value="last_30_days">{t("fase2.last_30d")}</SelectItem>
              <SelectItem value="ytd">YTD</SelectItem>
            </SelectContent>
          </Select>
          <Select value={concello} onValueChange={setConcello}>
            <SelectTrigger className="h-10 w-[220px]" data-testid="esg-concello">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("fase2.all_concellos")}</SelectItem>
              {concellos.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <EsgDownloads indicators={indicators} values={values} lang={i18n.language} />
        </div>
      </div>

      {totalKpis > 0 && (
        <div className="mb-6" data-testid="esg-state-strip">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1"
              data-testid="esg-count-active"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {i18n.language.startsWith("es") ? "Activos" : "Activas"}:{" "}
              <b className="tabular-nums">{activeList.length}</b>
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1"
              data-testid="esg-count-provisional"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {i18n.language.startsWith("es")
                ? "Provisionales"
                : "Provisionais"}
              : <b className="tabular-nums">{provisional.length}</b>
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral-50 text-neutral-700 border border-neutral-200 px-3 py-1"
              data-testid="esg-count-blocked"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  blockedList.length ? "bg-rose-500" : "bg-neutral-400"
                }`}
              />
              {i18n.language.startsWith("es") ? "Bloqueados" : "Bloqueados"}:{" "}
              <b className="tabular-nums">{blockedList.length}</b>
            </span>
            <span className="text-salnes-stone">
              / {totalKpis} KPIs
            </span>
          </div>
        </div>
      )}

      {(blockedList.length > 0 || provisional.length > 0) && (
        <div className="mb-6">
          <ScopeBanner data-testid="esg-scope-banner">
            {provisional.length > 0 && (
              <>
                {i18n.language.startsWith("es") ? "Estimación operativa" : "Estimación operativa"}: {provisional.length}/{totalKpis}.{" "}
              </>
            )}
            {blockedList.length > 0 && (
              <>
                {i18n.language.startsWith("es") ? "Bloqueados por gaps upstream" : "Bloqueados por gaps upstream"}: {blockedList.length}/{totalKpis}.
              </>
            )}
            {" "}{t("fase2.empty_state_sub")}
          </ScopeBanner>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(indicators.length === 0
          ? Array.from({ length: 8 }).map((_, i) => i)
          : indicators
        ).map((ind, idx) =>
          typeof ind === "number" ? (
            <Skeleton key={idx} className="h-[220px] rounded-2xl" />
          ) : (
            <EsgKpiCard
              key={ind.code}
              indicator={ind}
              value={values[ind.code]}
              i18nLang={i18n.language}
              index={idx}
            />
          )
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <ByConcelloBar data={byConcello} />
        <MonthlyEvolutionChart data={monthly?.data || []} />
      </div>
      <div className="mt-4">
        <ComparativeBar
          data={byConcello.map((c) => ({
            name: c.name,
            population: c.population,
            detections: c.detections,
          }))}
        />
      </div>
    </div>
  );
}
